'use client';

import { type ReactNode, type RefObject } from 'react';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { Spinner } from './loading/Spinner';

type PullToRefreshProps = {
  children: ReactNode;
  onRefresh: () => Promise<void> | void;
  scrollRef: RefObject<HTMLElement | null>;
};

export const PullToRefresh = ({ children, onRefresh, scrollRef }: PullToRefreshProps) => {
  const { pullProgress, pullDistance, isRefreshing, isPulling, isEnabled, handlers } = usePullToRefresh({
    onRefresh,
    scrollRef,
  });

  const shouldShowIndicator = isEnabled && (isPulling || isRefreshing);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        className={`pointer-events-none absolute left-0 right-0 top-0 z-20 flex justify-center transition-opacity duration-150 ${shouldShowIndicator ? 'opacity-100' : 'opacity-0'}`}
        style={{ transform: `translateY(${Math.min(pullDistance, 72)}px)` }}
      >
        <div className="mt-2 flex items-center gap-2 rounded-full border border-base-300 bg-base-100 px-3 py-1.5 text-xs text-[var(--color-fg-muted)] shadow-xs">
          <Spinner className="text-primary" />
          <span>{isRefreshing ? 'Refreshing...' : `${Math.round(pullProgress * 100)}%`}</span>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col" {...handlers}>
        {children}
      </div>
    </div>
  );
};
